import classNames from "classnames";
import { Countries } from "types/Models/countries";
import { joinLegalName } from "v2/utils/joinLegalName";
import { OnboardingEntityDraft } from "../../OnboardingDataContext";
import {
  buildAddressString,
  getCountryCodeForType,
} from "./entityFieldOptions";
import { EntityCard } from "./EntityCard";
import { EntityTreeNode } from "./buildEntityTree";

type Connector = "elbow" | "through";

type RelationTag = "Parent" | "Subsidiary";

type EntitySubtreeProps = {
  node: EntityTreeNode;
  connector?: Connector;
  countries: Countries[] | undefined;
  onEditPrimary: () => void;
  onEdit: (entity: OnboardingEntityDraft) => void;
  onRemove: (entity: OnboardingEntityDraft) => void;
};

export const EntitySubtree = ({
  node,
  connector,
  countries,
  onEditPrimary,
  onEdit,
  onRemove,
}: EntitySubtreeProps) => {
  const { entity, children } = node;
  const isPrimary = Boolean(entity.isPrimary);
  let relationTag: RelationTag | undefined;
  if (connector) relationTag = "Subsidiary";
  else if (children.length > 0) relationTag = "Parent";

  return (
    <div className={classNames("t-relative", { "t-pl-10": connector })}>
      {connector === "elbow" && (
        <span className="t-pointer-events-none t-absolute -t-top-4 t-left-4 t-h-12 t-w-6 t-rounded-bl-lg t-border-0 t-border-b t-border-l t-border-solid t-border-neutral-20" />
      )}
      {connector === "through" && (
        <>
          <span className="t-pointer-events-none t-absolute -t-bottom-4 -t-top-4 t-left-4 t-border-0 t-border-l t-border-solid t-border-neutral-20" />
          <span className="t-pointer-events-none t-absolute t-left-4 t-top-8 t-w-6 t-border-0 t-border-b t-border-solid t-border-neutral-20" />
        </>
      )}

      <EntityCard
        isPrimary={isPrimary}
        name={
          entity.registeredName ||
          joinLegalName(entity.entityName, entity.entitySuffix)
        }
        entityTypeLabel={entity.entityTypeLabel}
        addressString={
          entity.addressData ? buildAddressString(entity.addressData) : ""
        }
        countryCode={getCountryCodeForType(countries, entity.formationTypeId)}
        relationTag={relationTag}
        onNotMyCompany={isPrimary ? onEditPrimary : undefined}
        onEdit={isPrimary ? onEditPrimary : () => onEdit(entity)}
        onRemove={isPrimary ? undefined : () => onRemove(entity)}
      />

      {children.length > 0 && (
        <div className="t-mt-4 t-flex t-flex-col t-gap-4">
          {children.map((child, index) => (
            <EntitySubtree
              key={child.entity.localId}
              node={child}
              connector={index === children.length - 1 ? "elbow" : "through"}
              countries={countries}
              onEditPrimary={onEditPrimary}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
};
